type Band = 1 | 2;
type Tier = 'A' | 'B' | 'C' | 'D';

export interface BandAssignment {
  band: Band;
  tier: Tier | null;
}

// Keywords are matched as whole words, not substrings — substring matching
// misfires on titles like "Cofounder" (contains "founder"), "Global
// Headquarters" (contains "global head") or "Managing Director, Reports Tools"
// (contains "reports to", which would wrongly exclude a genuine MD).
const EXCLUSION_PATTERNS = [
  /\bPA to\b/i,
  /\bEA to\b/i,
  /\bassistant to\b/i,
  /\bpersonal assistant\b/i,
  /\bexecutive assistant\b/i,
  /\bsecretary to\b/i,
  /\bsupport to\b/i,
  /\breports to\b/i,
  /\boffice of the\b/i,
];

// C-suite abbreviations stay case-sensitive so e.g. "ceo" inside a lowercase
// word never matches.
const STANDALONE_CSUITE = /\b(CEO|CFO|CIO|COO|CMO|CCO)\b/;

function isExcluded(jobTitle: string): boolean {
  return EXCLUSION_PATTERNS.some((pattern) => pattern.test(jobTitle));
}

function isTierA(jobTitle: string): boolean {
  if (/\bchief\b/i.test(jobTitle) && /\bofficer\b/i.test(jobTitle)) return true;
  if (/\bchief executive\b/i.test(jobTitle)) return true;
  return STANDALONE_CSUITE.test(jobTitle);
}

function isTierB(jobTitle: string): boolean {
  return /\bmanaging director\b/i.test(jobTitle) || /\bmanaging partner\b/i.test(jobTitle);
}

function isTierC(jobTitle: string): boolean {
  // "President" is a board-level role, but "Vice President" (and SVP/EVP variants)
  // are mid-level and belong in Band 2 — don't let the substring promote them.
  const isPresident =
    /\bpresident\b/i.test(jobTitle) && !/\bvice[\s-]?president\b/i.test(jobTitle);
  return (
    /\bgeneral partner\b/i.test(jobTitle) ||
    /\bchair(man|woman|person)\b/i.test(jobTitle) ||
    isPresident ||
    /\bfounder\b/i.test(jobTitle)
  );
}

function isTierD(jobTitle: string): boolean {
  return /\bglobal head\b/i.test(jobTitle) || /\bgroup head\b/i.test(jobTitle);
}

export function assignBand(jobTitle: string | undefined | null): BandAssignment {
  if (!jobTitle) return { band: 2, tier: null };
  // Match on what the page renders: innerText collapses whitespace runs, so a
  // double-spaced "PA  to" must behave exactly like the rendered "PA to".
  const title = jobTitle.replace(/\s+/g, ' ').trim();
  if (!title) return { band: 2, tier: null };
  if (isExcluded(title)) return { band: 2, tier: null };
  if (isTierA(title)) return { band: 1, tier: 'A' };
  if (isTierB(title)) return { band: 1, tier: 'B' };
  if (isTierC(title)) return { band: 1, tier: 'C' };
  if (isTierD(title)) return { band: 1, tier: 'D' };
  return { band: 2, tier: null };
}

const TIER_ORDER: Record<Tier, number> = { A: 1, B: 2, C: 3, D: 4 };

function bandToSortNumber({ band, tier }: BandAssignment): number {
  if (band === 2 || tier === null) return 5;
  return TIER_ORDER[tier];
}

function compareField(a: any, b: any): number {
  if (typeof a !== 'string' || typeof b !== 'string') return 0;
  return a.localeCompare(b);
}

// Alphabetical by surname, then given name as a tie-breaker so contacts who
// share a surname get a stable, human-meaningful order instead of falling
// through to arbitrary API order.
function byName(
  a: { familyName?: any; givenName?: any },
  b: { familyName?: any; givenName?: any },
): number {
  const surname = compareField(a.familyName, b.familyName);
  if (surname !== 0) return surname;
  return compareField(a.givenName, b.givenName);
}

export function sortContacts<
  T extends { jobTitle?: string; familyName?: any; givenName?: any; keyContact?: boolean }
>(contacts: T[]): T[] {
  return [...contacts].sort((a, b) => {
    const aKey = !!a.keyContact;
    const bKey = !!b.keyContact;
    if (aKey !== bKey) return aKey ? -1 : 1;
    // Both are key contacts: sort alphabetically only
    if (aKey && bKey) return byName(a, b);
    // Both are non-key: sort by band/tier, then alphabetically
    const diff =
      bandToSortNumber(assignBand(a.jobTitle)) -
      bandToSortNumber(assignBand(b.jobTitle));
    return diff !== 0 ? diff : byName(a, b);
  });
}
